<script setup>
import { useThemeConfig } from '@core/composable/useThemeConfig';

const props = defineProps({
  defaultValue: {
    type: Boolean,
    required: false,
    default: false,
  },
  themes: {
    type: Array,
    required: true,
    default: () => [],
  },
});
const emit = defineEmits(['changeTheme']);
import MoonIcon from '@/components/ui/icons/Moon.vue';
import SunIcon from '@/components/ui/icons/Sun.vue';

const { theme } = useThemeConfig();

const {
  state: currentThemeName,
  next: getNextThemeName,
  index: currentThemeIndex,
} = useCycleList(
  props.themes.map((t) => t.name),
  { initialValue: theme.value },
);

const changeTheme = () => {
  // Don't flip `theme` (the Vuetify/core layer) here: doing so kicks Vuetify's reactive
  // theme change on a microtask BEFORE the store's View Transition captures its "old"
  // snapshot, so the cards (Vuetify) would already be dark while the rest is still light
  // — the staggered flash. Let the store own the whole switch; the icon follows via the
  // watch(theme) below once the store flips it inside the transition.
  emit('changeTheme');
};

// Update icon if theme is changed from other sources
watch(theme, (val) => {
  currentThemeName.value = val;
});
</script>

<template>
  <div class="toggle-switch">
    <label>
      <input
        type="checkbox"
        :checked="currentThemeName === 'light'"
        @change="changeTheme"
      >
      <span class="slider">
        <span class="toggle">
          <Transition name="icon">
            <SunIcon
              v-if="currentThemeName === 'light'"
              class="toggle__icon toggle__icon--sun"
            />
            <MoonIcon
              v-else
              class="toggle__icon toggle__icon--moon"
            />
          </Transition>
        </span>
      </span>
    </label>
  </div>
</template>

<style lang="scss" scoped>
.toggle-switch {
  position: relative;
  min-width: 64px;
  width: 64px;
  max-width: 64px;
  height: 40px;
  @media (max-width: 768px) {
    min-width: 46px;
    width: 46px;
    max-width: 46px;
    height: 30px;
  }
}

input {
  display: none;
}

.slider {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transition: var(--transition-fast);
  border-radius: 50px;
  background-color: var(--sub-bg);
  cursor: pointer;
}

.slider .toggle {
  content: '';
  display: flex;
  position: absolute;
  top: 50%;
  left: 4px;
  justify-content: center;
  align-items: center;
  transform: translate(0, -50%);
  transition: all var(--transition-fast);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  background-color: var(--wp-surface-default);

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    left: 3px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
}

input:checked ~ .slider .toggle {
  left: 28px;
  box-shadow: none;

  @media (max-width: 768px) {
    left: 23px;
  }
}

.icon-enter-active,
.icon-leave-active {
  transition: all var(--transition-fast) ease-out;
}

.icon-enter-from {
  position: absolute;
  opacity: 0;
}

.icon-leave-to {
  position: absolute;
  opacity: 0;
}
</style>
